import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { firebaseUser } = useAuth();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.products}/${id}`);
      setProduct(response.data);
      if (response.data.variants.length > 0) {
        setSelectedVariant(response.data.variants[0]);
      }
    } catch (error) {
      setError('Failed to fetch product details');
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVariantChange = (variant) => {
    setSelectedVariant(variant);
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0 && value <= selectedVariant.stock) {
      setQuantity(value);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    
    if (!firebaseUser) {
      navigate('/login');
      return;
    }

    const success = await addToCart(product._id, {
      color: selectedVariant.color,
      size: selectedVariant.size
    }, quantity);

    if (success) {
      navigate('/cart');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={fetchProduct}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-4">Product not found</h2>
        <button
          onClick={() => navigate('/products')}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
        >
          Back to Products
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="md:flex">
          <div className="md:w-1/2">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-96 object-cover"
            />
          </div>
          <div className="md:w-1/2 p-6">
            <h1 className="text-3xl font-bold mb-4">{product.name}</h1>
            <p className="text-gray-600 mb-6">{product.description}</p>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Variants</h2>
              <div className="grid grid-cols-2 gap-4">
                {product.variants.map((variant) => (
                  <button
                    key={`${variant.color}-${variant.size}`}
                    onClick={() => handleVariantChange(variant)}
                    className={`p-4 border rounded-lg ${
                      selectedVariant === variant
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="font-medium">{variant.color}</div>
                    <div className="text-sm text-gray-600">{variant.size}</div>
                    <div className="text-lg font-bold mt-2">₹{variant.price.toLocaleString('en-IN')}</div>
                    <div className="text-sm text-gray-500">
                      Stock: {variant.stock}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedVariant && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  max={selectedVariant.stock}
                  value={quantity}
                  onChange={handleQuantityChange}
                  className="w-24 px-3 py-2 border rounded-lg"
                />
              </div>
            )}

            <button
              onClick={handleAddToCart}
              disabled={!selectedVariant}
              className={`w-full py-3 px-6 rounded-lg font-semibold ${
                selectedVariant
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {firebaseUser ? 'Add to Cart' : 'Sign in to Add to Cart'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail; 